/**
 * Визуальный табличный редактор плановых показателей БДР (Р-14) с поддержкой
 * вставки диапазонов напрямую из Excel (Ctrl+V / TSV) и человекочитаемым
 * отображением строк плана без необходимости ввода JSON.
 *
 * План БДР считается по месяцам (ТЗ Э0б, Д7): у каждой строки обязателен месяц
 * ГГГГ-ММ, объект ОСНОВЫ — по желанию (без текста объекта backend подставит его
 * имя).
 */
import { useState, type ClipboardEvent } from 'react';
import styled from 'styled-components';
import type { EconPlanLine, NewEconPlanLine } from '@shared/api';
import { Button } from '@shared/ui/Button';

/** Объект ОСНОВЫ для выбора в строке плана. */
export interface PlanProjectOption {
  id: string;
  name: string;
  code: string | null;
}

/** Месяц плана: строго ГГГГ-ММ (как проверяет backend). */
const PLAN_MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

function projectLabel(p: PlanProjectOption): string {
  return p.code ? `${p.code} — ${p.name}` : p.name;
}

const EditorWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const PasteZone = styled.textarea`
  width: 100%;
  min-height: 76px;
  padding: 10px 12px;
  border: 1px dashed ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 12.5px;
  line-height: 1.45;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const GridScroll = styled.div`
  overflow-x: auto;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
`;

const GridTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    padding: 8px 10px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    text-align: left;
    vertical-align: middle;
  }

  th {
    background: ${({ theme }) => theme.colors.surface};
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${({ theme }) => theme.colors.text.muted};
    font-weight: 600;
  }
`;

const CellInput = styled.input`
  width: 100%;
  min-width: 110px;
  padding: 6px 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 13px;
`;

const CellSelect = styled.select`
  width: 100%;
  min-width: 140px;
  padding: 6px 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 13px;
`;

const IconBtn = styled.button`
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.muted};
  border-radius: 6px;
  padding: 4px 8px;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.status.danger};
    border-color: ${({ theme }) => theme.colors.status.danger};
  }
`;

const Hint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

export const PLAN_METRIC_OPTIONS = [
  { value: 'revenue', label: 'Доход (выручка)' },
  { value: 'cost', label: 'Затраты' },
  { value: 'man_hours', label: 'Человеко-часы' },
  { value: 'machine_hours', label: 'Машино-часы' },
] as const;

export interface PlanEditorRow {
  object: string;
  metric: string;
  costArticle: string;
  planValue: string;
  periodMonth: string;
  /** Объект ОСНОВЫ (`pto_projects.id`); пустая строка — не привязан. */
  projectId: string;
}

export function createEmptyPlanRow(): PlanEditorRow {
  return {
    object: '',
    metric: 'revenue',
    costArticle: '',
    planValue: '',
    periodMonth: '',
    projectId: '',
  };
}

/** Строка редактора заполнена хоть чем-то, кроме выбора показателя. */
function isFilledRow(r: PlanEditorRow): boolean {
  return Boolean(
    r.object.trim() ||
      r.planValue.trim() ||
      r.costArticle.trim() ||
      r.periodMonth.trim() ||
      r.projectId,
  );
}

/**
 * Разбор числа в русском/бухгалтерском формате:
 * удаляет пробелы и неразрывные пробелы (\u00A0, \u202F), заменяет запятую на точку.
 */
export function parseRussianNumber(raw: string): number {
  const cleaned = raw
    .replace(/[\s\u00A0\u202F]/g, '')
    .replace(/₽|руб\.?/gi, '')
    .replace(',', '.');
  if (!cleaned) return Number.NaN;
  return Number(cleaned);
}

/**
 * Нормализация кода или русского названия метрики при вставке из Excel.
 */
export function parseMetric(raw: string): string {
  const norm = raw.trim().toLowerCase();
  if (
    norm === 'revenue' ||
    norm.includes('доход') ||
    norm.includes('выручк') ||
    norm.includes('кс-2') ||
    norm.includes('кс-3')
  ) {
    return 'revenue';
  }
  if (norm === 'cost' || norm.includes('затрат') || norm.includes('расход') || norm.includes('себест')) {
    return 'cost';
  }
  if (norm === 'man_hours' || norm.includes('чел') || norm.includes('фовр') || norm.includes('труд')) {
    return 'man_hours';
  }
  if (norm === 'machine_hours' || norm.includes('маш') || norm.includes('техник')) {
    return 'machine_hours';
  }
  return 'cost';
}

/**
 * Разбор TSV-текста из буфера обмена Excel.
 * Ожидаемый порядок колонок: Объект | Показатель (или Статья) | Статья затрат | Сумма плана | Период (ГГГГ-ММ).
 * Также поддерживает 3-колоночный формат: Объект | Статья затрат | Сумма плана.
 */
export function parseTsvClipboard(text: string): PlanEditorRow[] {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  const parsed: PlanEditorRow[] = [];

  for (const line of lines) {
    const cols = line.split('\t').map((c) => c.trim());
    if (cols.length < 2) continue;

    // Пропускаем строку-заголовок, если первая или последняя колонка явно текстовая шапка
    const firstLower = cols[0].toLowerCase();
    if (firstLower === 'объект' || firstLower === 'object' || firstLower === 'наименование') {
      continue;
    }

    if (cols.length === 2) {
      // Формат: Статья затрат | План
      const metric = parseMetric(cols[0]);
      parsed.push({
        object: '',
        metric,
        costArticle: metric === 'cost' ? cols[0] : '',
        planValue: cols[1],
        periodMonth: '',
        projectId: '',
      });
    } else if (cols.length === 3) {
      // Формат: Объект | Статья/Показатель | План
      const metric = parseMetric(cols[1]);
      parsed.push({
        object: cols[0],
        metric,
        costArticle: metric === 'cost' ? cols[1] : '',
        planValue: cols[2],
        periodMonth: '',
        projectId: '',
      });
    } else {
      // Формат: Объект | Показатель | Статья затрат | План | [Период]
      const metric = parseMetric(cols[1]);
      parsed.push({
        object: cols[0],
        metric,
        costArticle: cols[2] || (metric === 'cost' ? cols[1] : ''),
        planValue: cols[3] ?? '',
        periodMonth: cols[4] ?? '',
        projectId: '',
      });
    }
  }
  return parsed;
}

export function VisualPlanEditor({
  rows,
  onChange,
  projects = [],
}: {
  rows: PlanEditorRow[];
  onChange: (next: PlanEditorRow[]) => void;
  /** Объекты ОСНОВЫ для привязки строки (`project_id`). */
  projects?: readonly PlanProjectOption[];
}) {
  const [pasteBuffer, setPasteBuffer] = useState('');

  function updateRow(idx: number, patch: Partial<PlanEditorRow>) {
    onChange(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }

  function removeRow(idx: number) {
    if (rows.length <= 1) {
      onChange([createEmptyPlanRow()]);
      return;
    }
    onChange(rows.filter((_, i) => i !== idx));
  }

  function handlePaste(e: ClipboardEvent<HTMLTextAreaElement>) {
    const text = e.clipboardData.getData('text/plain');
    if (!text || !text.includes('\t')) return;
    const imported = parseTsvClipboard(text);
    if (imported.length > 0) {
      e.preventDefault();
      onChange([...rows.filter(isFilledRow), ...imported]);
      setPasteBuffer('');
    }
  }

  function applyManualPaste() {
    if (!pasteBuffer.trim()) return;
    const imported = parseTsvClipboard(pasteBuffer);
    if (imported.length > 0) {
      onChange([...rows.filter(isFilledRow), ...imported]);
      setPasteBuffer('');
    }
  }

  return (
    <EditorWrap>
      <Hint>
        Скопируйте диапазон ячеек в Excel и вставьте (Ctrl+V) в поле ниже либо заполните строки
        в таблице вручную. Поддерживаются форматы колонок:{' '}
        <b>Объект → Статья/Показатель → Сумма</b> или{' '}
        <b>Объект → Показатель → Статья затрат → Сумма → Месяц (ГГГГ-ММ)</b>. План БДР
        считается по месяцам — месяц обязателен в каждой строке.
      </Hint>
      <PasteZone
        placeholder="Вставьте сюда скопированные строки из Excel (Ctrl+V)…"
        value={pasteBuffer}
        onChange={(e) => setPasteBuffer(e.target.value)}
        onPaste={handlePaste}
      />
      {pasteBuffer.trim() && (
        <div>
          <Button type="button" $variant="secondary" $size="sm" onClick={applyManualPaste}>
            Разобрать вставленные строки
          </Button>
        </div>
      )}

      <GridScroll>
        <GridTable>
          <thead>
            <tr>
              <th>№</th>
              <th>Объект ОСНОВЫ</th>
              <th>Объект</th>
              <th>Показатель</th>
              <th>Статья затрат</th>
              <th>Плановое значение</th>
              <th>Месяц (ГГГГ-ММ)</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td>{i + 1}</td>
                <td>
                  <CellSelect
                    aria-label={`Объект ОСНОВЫ строки ${i + 1}`}
                    value={r.projectId}
                    onChange={(e) => updateRow(i, { projectId: e.target.value })}
                  >
                    <option value="">— не привязан —</option>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {projectLabel(p)}
                      </option>
                    ))}
                  </CellSelect>
                </td>
                <td>
                  <CellInput
                    placeholder={r.projectId ? 'Как у объекта ОСНОВЫ' : 'Напр. ОЗХ'}
                    value={r.object}
                    onChange={(e) => updateRow(i, { object: e.target.value })}
                  />
                </td>
                <td>
                  <CellSelect
                    value={r.metric}
                    onChange={(e) => updateRow(i, { metric: e.target.value })}
                  >
                    {PLAN_METRIC_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </CellSelect>
                </td>
                <td>
                  <CellInput
                    placeholder={r.metric === 'cost' ? 'Напр. материалы' : '—'}
                    disabled={r.metric !== 'cost'}
                    value={r.metric === 'cost' ? r.costArticle : ''}
                    onChange={(e) => updateRow(i, { costArticle: e.target.value })}
                  />
                </td>
                <td>
                  <CellInput
                    inputMode="decimal"
                    placeholder="0,00"
                    value={r.planValue}
                    onChange={(e) => updateRow(i, { planValue: e.target.value })}
                  />
                </td>
                <td>
                  <CellInput
                    placeholder="2026-07"
                    aria-label={`Месяц плана строки ${i + 1}`}
                    aria-invalid={
                      r.periodMonth.trim() !== '' && !PLAN_MONTH_RE.test(r.periodMonth.trim())
                    }
                    value={r.periodMonth}
                    onChange={(e) => updateRow(i, { periodMonth: e.target.value })}
                  />
                </td>
                <td>
                  <IconBtn
                    type="button"
                    onClick={() => removeRow(i)}
                    title="Удалить строку"
                    aria-label={`Удалить строку ${i + 1}`}
                  >
                    ✕
                  </IconBtn>
                </td>
              </tr>
            ))}
          </tbody>
        </GridTable>
      </GridScroll>

      <Toolbar>
        <Button
          type="button"
          $variant="ghost"
          $size="sm"
          onClick={() => onChange([...rows, createEmptyPlanRow()])}
        >
          + Добавить строку
        </Button>
        <Hint>Заполнено строк: {rows.filter(isFilledRow).length}</Hint>
      </Toolbar>
    </EditorWrap>
  );
}

/**
 * Конвертация визуальных строк редактора в DTO создания версии плана с валидацией.
 */
export function buildPlanLinesFromEditor(
  rows: PlanEditorRow[],
): { lines: NewEconPlanLine[]; error: string | null } {
  const built: NewEconPlanLine[] = [];
  for (let i = 0; i < rows.length; i += 1) {
    const r = rows[i];
    if (!isFilledRow(r)) continue;
    if (!r.metric) {
      return { lines: [], error: `Строка ${i + 1}: выберите показатель.` };
    }
    const val = parseRussianNumber(r.planValue);
    if (!Number.isFinite(val)) {
      return { lines: [], error: `Строка ${i + 1}: плановое значение должно быть числом.` };
    }
    if (r.metric === 'cost' && !r.costArticle.trim()) {
      return { lines: [], error: `Строка ${i + 1}: для затрат укажите статью затрат.` };
    }
    const month = r.periodMonth.trim();
    if (!month) {
      return {
        lines: [],
        error: `Строка ${i + 1}: укажите месяц плана (ГГГГ-ММ) — план БДР считается по месяцам.`,
      };
    }
    if (!PLAN_MONTH_RE.test(month)) {
      return { lines: [], error: `Строка ${i + 1}: месяц «${month}» — в формате ГГГГ-ММ.` };
    }
    const line: NewEconPlanLine = { metric: r.metric, plan_value: val, period_month: month };
    if (r.object.trim()) line.object = r.object.trim();
    if (r.metric === 'cost' && r.costArticle.trim()) line.cost_article = r.costArticle.trim();
    if (r.projectId) line.project_id = r.projectId;
    built.push(line);
  }
  if (built.length === 0) {
    return { lines: [], error: 'Добавьте хотя бы одну строку плана.' };
  }
  return { lines: built, error: null };
}

const ValueCell = styled.td`
  font-variant-numeric: tabular-nums;
  font-weight: 600;
`;

/**
 * Таблица просмотра сохранённых строк версии плана (вместо сырого JSON).
 * Строки без месяца (старые версии) помечены: в план месяца они не входят.
 */
export function PlanVersionLinesTable({
  lines,
  projects = [],
}: {
  lines: EconPlanLine[];
  projects?: readonly PlanProjectOption[];
}) {
  if (lines.length === 0) {
    return <Hint>В этой версии плана нет строк.</Hint>;
  }
  const metricLabel = (m: string) =>
    PLAN_METRIC_OPTIONS.find((o) => o.value === m)?.label ?? m;
  const projectName = (id: string | null) => {
    if (!id) return '—';
    const p = projects.find((x) => x.id === id);
    return p ? projectLabel(p) : 'объект ОСНОВЫ';
  };

  return (
    <GridScroll>
      <GridTable>
        <thead>
          <tr>
            <th>Месяц</th>
            <th>Объект ОСНОВЫ</th>
            <th>Объект</th>
            <th>Показатель</th>
            <th>Статья затрат</th>
            <th>Плановое значение</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => (
            <tr key={l.id}>
              <td>{l.period_month || 'без месяца — не входит в план месяца'}</td>
              <td>{projectName(l.project_id)}</td>
              <td>{l.object || '—'}</td>
              <td>{metricLabel(l.metric)}</td>
              <td>{l.cost_article || '—'}</td>
              <ValueCell>{l.plan_value.toLocaleString('ru-RU')}</ValueCell>
            </tr>
          ))}
        </tbody>
      </GridTable>
    </GridScroll>
  );
}
